import { useState, useCallback, useRef, useEffect } from 'react';

export interface MutationOptions<TData = unknown, TError = Error, TVariables = void> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: TError, variables: TVariables) => void;
  onSettled?: (data: TData | undefined, error: TError | null, variables: TVariables) => void;
}

export interface MutationState<TData = unknown, TError = Error> {
  data: TData | undefined;
  error: TError | null;
  isIdle: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

export interface MutationResult<TData = unknown, TError = Error, TVariables = void>
  extends MutationState<TData, TError> {
  mutate: (variables: TVariables) => Promise<void>;
  mutateAsync: (variables: TVariables) => Promise<TData>;
  reset: () => void;
}

export function useMutation<TData = unknown, TError = Error, TVariables = void>(
  options: MutationOptions<TData, TError, TVariables>
): MutationResult<TData, TError, TVariables> {
  const { mutationFn, onSuccess, onError, onSettled } = options;

  const [state, setState] = useState<MutationState<TData, TError>>({
    data: undefined,
    error: null,
    isIdle: true,
    isLoading: false,
    isSuccess: false,
    isError: false,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    setState({
      data: undefined,
      error: null,
      isIdle: true,
      isLoading: false,
      isSuccess: false,
      isError: false,
    });
  }, []);

  const mutateAsync = useCallback(
    async (variables: TVariables): Promise<TData> => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setState({
        data: undefined,
        error: null,
        isIdle: false,
        isLoading: true,
        isSuccess: false,
        isError: false,
      });

      try {
        const data = await mutationFn(variables);

        if (!isMountedRef.current) return data;

        setState({
          data,
          error: null,
          isIdle: false,
          isLoading: false,
          isSuccess: true,
          isError: false,
        });

        onSuccess?.(data, variables);
        onSettled?.(data, null, variables);

        return data;
      } catch (error) {
        const err = error as TError;

        if (!isMountedRef.current) throw err;

        setState({
          data: undefined,
          error: err,
          isIdle: false,
          isLoading: false,
          isSuccess: false,
          isError: true,
        });

        onError?.(err, variables);
        onSettled?.(undefined, err, variables);

        throw err;
      }
    },
    [mutationFn, onSuccess, onError, onSettled]
  );

  const mutate = useCallback(
    async (variables: TVariables): Promise<void> => {
      try {
        await mutateAsync(variables);
      } catch {
        // Error already handled in mutateAsync
      }
    },
    [mutateAsync]
  );

  return {
    ...state,
    mutate,
    mutateAsync,
    reset,
  };
}
